/** Application shell — DEMO version with DemoBanner. */

import { useCallback, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useSession } from './session';
import type { DemoRole } from './session';
import { AdminPage } from '@/features/admin/AdminPage';
import { AgentWorkspace } from '@/features/agent/AgentWorkspace';
import { LoginPage } from '@/features/auth/LoginPage';
import { Wallboard } from '@/features/supervisor/Wallboard';
import { Badge, Button, EmptyState, LoadingState } from '@/ui/primitives';

export function App() {
  const { user, branding, demoInfo, demoRole, demoBootstrapping, demoSignIn, signOut, can } = useSession();

  if (!user) {
    return demoBootstrapping ? (
      <div className="flex h-full items-center justify-center bg-bg">
        <LoadingState label="Opening your selected demo workspace…" />
      </div>
    ) : <LoginPage />;
  }

  const defaultWorkspace = can('user:view')
    ? '/admin'
    : can('supervisor:wallboard')
      ? '/supervisor'
      : '/agent';

  return (
    <div className="flex h-full flex-col">
      {demoInfo?.demo_mode ? (
        <DemoBanner
          demoInfo={demoInfo}
          currentRole={demoRole}
          onSwitchRole={demoSignIn}
          onSignOut={signOut}
        />
      ) : null}

      <header className="flex h-topbar shrink-0 items-center gap-4 border-b border-line bg-surface px-3">
        <span className="text-md font-semibold">{branding?.product_name ?? 'Konnextions'}</span>
        {branding?.is_demo ? <Badge tone="info">demo tenant</Badge> : null}

        <nav className="flex items-center gap-1" aria-label="Primary">
          <NavItem to="/agent" label="Agent" />
          {can('supervisor:wallboard') ? <NavItem to="/supervisor" label="Supervisor" /> : null}
          {can('user:view') ? <NavItem to="/admin" label="Admin" /> : null}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-muted">{user.full_name}</span>
          <span className="text-2xs uppercase tracking-wide text-faint">
            {user.roles.join(', ')}
          </span>
          <Button size="sm" variant="outline" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </header>

      <main className="flex min-h-0 flex-1 flex-col">
        <Routes>
          <Route path="/" element={<Navigate to={defaultWorkspace} replace />} />
          <Route path="/agent" element={<AgentWorkspace />} />
          <Route
            path="/supervisor"
            element={
              can('supervisor:wallboard') ? (
                <Wallboard />
              ) : (
                <EmptyState
                  message="Your role does not include supervisor:wallboard"
                  hint="Ask an administrator to grant the capability."
                />
              )
            }
          />
          <Route
            path="/admin"
            element={
              can('user:view') ? (
                <AdminPage />
              ) : (
                <EmptyState
                  message="Your role does not include user:view"
                  hint="Ask an administrator to grant the capability."
                />
              )
            }
          />
          <Route path="*" element={<EmptyState message="Page not found" />} />
        </Routes>
      </main>
    </div>
  );
}

function DemoBanner({
  demoInfo,
  currentRole,
  onSwitchRole,
  onSignOut,
}: {
  demoInfo: { demo_mode: boolean; remaining_minutes: number; roles: DemoRole[] };
  currentRole: string | null;
  onSwitchRole: (role: string) => Promise<void>;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const [switching, setSwitching] = useState<string | null>(null);

  const handleSwitch = useCallback(
    async (slug: string) => {
      if (slug === currentRole || switching) return;
      setSwitching(slug);
      try {
        await onSwitchRole(slug);
        navigate(demoWorkspaceFor(slug), { replace: true });
      } finally {
        setSwitching(null);
      }
    },
    [currentRole, switching, onSwitchRole, navigate],
  );

  return (
    <div
      className="flex shrink-0 items-center gap-3 px-3 py-1.5"
      style={{ background: 'linear-gradient(90deg, #6D5EF5 0%, #A3E635 100%)' }}
    >
      <span className="rounded-md bg-black/80 px-2 py-0.5 text-xs font-bold tracking-widest text-white uppercase">
        DEMO
      </span>
      <span className="text-xs font-medium text-white/90">
        {demoInfo.remaining_minutes}m remaining
      </span>

      <div className="ml-2 flex items-center gap-1">
        {demoInfo.roles.map((role) => (
          <button
            key={role.slug}
            onClick={() => handleSwitch(role.slug)}
            disabled={switching !== null}
            className="rounded-md px-2 py-0.5 text-xs font-semibold transition-all duration-150"
            style={{
              backgroundColor:
                currentRole === role.slug ? role.color : 'rgba(255,255,255,0.15)',
              color: currentRole === role.slug ? '#000' : 'rgba(255,255,255,0.9)',
              opacity: switching === role.slug ? 0.6 : 1,
            }}
            title={role.description}
          >
            {switching === role.slug ? '...' : role.label}
          </button>
        ))}
      </div>

      <button
        onClick={onSignOut}
        className="ml-auto rounded-md bg-white/15 px-2 py-0.5 text-xs font-medium text-white/90 transition-colors hover:bg-white/25"
      >
        Exit Demo
      </button>
    </div>
  );
}

function demoWorkspaceFor(role: string): string {
  if (role === 'owner' || role === 'admin') return '/admin';
  if (role === 'manager' || role === 'supervisor') return '/supervisor';
  return '/agent';
}

function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'rounded-md px-2 py-1 text-base transition-colors duration-base',
          isActive ? 'bg-surface-3 text-fg' : 'text-muted hover:bg-surface-2',
        ].join(' ')
      }
    >
      {label}
    </NavLink>
  );
}
